/**
 * The wire format for in-app entity links embedded in rich-text bodies.
 *
 * Landing pages (and article bodies) are authored as HTML in the admin's TipTap
 * editor and rendered by the RN client inside a WebView. A curator writing "the
 * Submariner Date is Rolex's diver" wants both of those to be taps that land on
 * the real screens, not web links that bounce the reader out to a browser. This
 * module owns the one contract that makes that work across the two codebases:
 *
 *   href === `watchkeeper://open/${data-wk-entity}/${data-wk-id}`
 *
 * Keeping the href a pure concatenation of the two data attributes is what lets
 * the WebView renderer intercept a click, read the `href` off the anchor, and
 * hand it straight to `navigateToDeepLink` without knowing anything about the
 * entity types — while the `data-wk-entity` attribute still tells it (and the
 * editor's own styling) that this anchor is ours and not an ordinary web link.
 *
 * Nothing here touches Firestore or Algolia; it is string handling only, so both
 * the TipTap mark and its tests can use it without a DOM.
 */

/** The four entities a curator can link to from a rich-text body. */
// `manufacturer` and `parent-org` match the app's link kinds (watchlock
// src/utils/entityLinks.ts, which already routes all six): a single doc id each
// — `movement_manufacturers/{id}` and `parent_organizations/{id}`.
export type EntityLinkType = 'reference' | 'brand' | 'calibre' | 'article' | 'manufacturer' | 'parent-org';

export const ENTITY_LINK_TYPES: readonly EntityLinkType[] = [
  'reference',
  'brand',
  'calibre',
  'article',
  'manufacturer',
  'parent-org',
] as const;

/** Picker tab labels, and the singular noun used in the editor's chip title. */
export const ENTITY_LINK_LABELS: Record<EntityLinkType, string> = {
  reference: 'Reference',
  brand: 'Brand',
  calibre: 'Calibre',
  article: 'Article',
  manufacturer: 'Manufacturer',
  'parent-org': 'Parent organisation',
};

/**
 * The scheme half of every entity href. `navigateToDeepLink` strips both this
 * and the `https://app.watchkeeper.tech` universal-link host, so either would
 * route; the custom scheme is used so a renderer that has NOT been taught about
 * entity links cannot mistake one for an ordinary web URL and open a browser.
 */
export const ENTITY_LINK_PREFIX = 'watchkeeper://open/';

/** The attribute a renderer matches on to recognise one of our anchors. */
export const ENTITY_TYPE_ATTR = 'data-wk-entity';
export const ENTITY_ID_ATTR = 'data-wk-id';
export const ENTITY_LABEL_ATTR = 'data-wk-label';

export interface EntityLinkTarget {
  type: EntityLinkType;
  /**
   * The href's id path — a single id for brands, calibres and articles, and the
   * two-segment `${brandId}/${referenceId}` for references. See
   * {@link referenceEntityId}.
   */
  id: string;
  /**
   * The entity's canonical name at the time of insertion. Held separately from
   * the link text because the text is curator-editable prose ("the Submariner
   * Date"), while the RN `ExploreCalibreDetail` route requires a real
   * `calibreName` param. Never used to resolve the entity — only to label it.
   */
  label?: string;
}

function isEntityLinkType(value: string): value is EntityLinkType {
  return (ENTITY_LINK_TYPES as readonly string[]).includes(value);
}

/**
 * A reference's address is two ids, not one. References live at
 * `watchBrands/{brandId}/references/{referenceId}`, the Algolia record uses the
 * bare `referenceId` as its `objectID` with `brandId` as a separate field, and
 * the RN `ExploreReferenceDetail` route takes both. So a reference entity id is
 * the two segments joined, which keeps the href a plain concatenation and leaves
 * the split to the route parser.
 */
export function referenceEntityId(
  brandId: string,
  referenceId: string,
): string {
  return `${brandId.trim()}/${referenceId.trim()}`;
}

/** The `{brandId, referenceId}` behind a reference entity id, or null. */
export function parseReferenceEntityId(
  id: string,
): { brandId: string; referenceId: string } | null {
  const idx = id.indexOf('/');
  if (idx <= 0) return null;
  const brandId = id.slice(0, idx);
  const referenceId = id.slice(idx + 1);
  if (!brandId || !referenceId) return null;
  return { brandId, referenceId };
}

/**
 * True when `id` is well formed for `type`. References need both segments;
 * everything else needs a single non-empty segment with no slash, so a
 * malformed two-segment brand id can never silently produce an href that the
 * route parser reads as something else.
 */
export function isValidEntityId(type: EntityLinkType, id: string): boolean {
  const trimmed = id.trim();
  if (!trimmed) return false;
  if (type === 'reference') return parseReferenceEntityId(trimmed) !== null;
  return !trimmed.includes('/');
}

/**
 * The deep link for an entity. Throws on a malformed id rather than emitting an
 * href that would dead-end in the app, because every caller builds this from a
 * picker result that is already known-good.
 */
export function buildEntityHref(type: EntityLinkType, id: string): string {
  const trimmed = id.trim();
  if (!isValidEntityId(type, trimmed)) {
    throw new Error(`Invalid ${type} entity id: "${id}"`);
  }
  return `${ENTITY_LINK_PREFIX}${type}/${trimmed}`;
}

/**
 * The inverse of {@link buildEntityHref}. Returns null for anything that is not
 * one of our links — an ordinary web URL, a deep link to a non-entity screen
 * such as `/subscription`, or an entity type this build does not know. Callers
 * treat null as "leave this anchor alone".
 */
export function parseEntityHref(href: string): EntityLinkTarget | null {
  if (!href.startsWith(ENTITY_LINK_PREFIX)) return null;
  const path = href.slice(ENTITY_LINK_PREFIX.length);
  const idx = path.indexOf('/');
  if (idx <= 0) return null;
  const type = path.slice(0, idx);
  const id = path.slice(idx + 1);
  if (!isEntityLinkType(type)) return null;
  if (!isValidEntityId(type, id)) return null;
  return { type, id };
}

/** The class pair the editor and the WebView renderer both style against. */
export function entityLinkClassName(type: EntityLinkType): string {
  return `wk-entity-link wk-entity-link--${type}`;
}
